import { Moon, Palette, Sun, Sprout, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeName, useTheme } from "@/hooks/useTheme";
import { useTranslation } from "react-i18next";

const OPTIONS: Array<{ id: ThemeName; labelKey: string; fallback: string; swatch: string; Icon: typeof Sun }> = [
  {
    id: "hardcourt",
    labelKey: "theme.hardcourt",
    fallback: "Hardcourt",
    swatch: "linear-gradient(135deg, hsl(215 60% 18%), hsl(200 90% 55%))",
    Icon: Square,
  },
  {
    id: "daylight",
    labelKey: "theme.daylight",
    fallback: "Daylight",
    swatch: "linear-gradient(135deg, hsl(40 40% 96%), hsl(145 60% 55%))",
    Icon: Sun,
  },
  {
    id: "grass",
    labelKey: "theme.grass",
    fallback: "Grass",
    swatch: "linear-gradient(135deg, hsl(135 50% 20%), hsl(75 95% 55%))",
    Icon: Sprout,
  },
  {
    id: "clay",
    labelKey: "theme.clay",
    fallback: "Clay",
    swatch: "linear-gradient(135deg, hsl(22 40% 18%), hsl(18 75% 45%))",
    Icon: Palette,
  },
  {
    id: "court",
    labelKey: "theme.court",
    fallback: "Court Night",
    swatch: "linear-gradient(135deg, hsl(220 30% 8%), hsl(165 50% 18%))",
    Icon: Moon,
  },
];

export default function ThemeSwitcher() {
  const { theme, setTheme } = useTheme();
  const { t } = useTranslation();
  const current = OPTIONS.find((o) => o.id === theme) ?? OPTIONS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="px-2 gap-1.5 md:gap-1.5 flex-col md:flex-row min-h-11 min-w-11 h-auto py-1"
          aria-label={`${t("theme.label", { defaultValue: "Theme" })}: ${t(current.labelKey, { defaultValue: current.fallback })}`}
        >
          <span
            aria-hidden
            className="inline-block w-4 h-4 rounded-full ring-1 ring-border"
            style={{ background: current.swatch }}
          />
          <span className="md:hidden text-[9px] leading-none tracking-wide text-muted-foreground">Theme</span>
          <current.Icon className="hidden md:inline w-3.5 h-3.5 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel className="text-xs">
          {t("theme.label", { defaultValue: "Theme" })}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.id}
            onClick={() => setTheme(o.id)}
            className="gap-2"
          >
            <span
              aria-hidden
              className="inline-block w-4 h-4 rounded-full ring-1 ring-border"
              style={{ background: o.swatch }}
            />
            <span className="flex-1 text-sm">
              {t(o.labelKey, { defaultValue: o.fallback })}
            </span>
            {theme === o.id && <span className="text-primary text-xs">●</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
